// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving inventory assets snapshot.
 *
 * Provides functionality to fetch inventory assets snapshot based on a specific date.
 * Each response covers a single cost currency: the backend-computed summary stats
 * for that currency plus the assets denominated in it.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import {
  InventoryAssetSnapshotItem,
  inventoryAssetSnapshotItemSchema,
} from '@/report/api/types';
import { parseArrayInChunks } from '@/utilities/chunked-parse';
import { DateTime } from 'luxon';
import { z } from 'zod';

/** Backend-computed summary stats for a single cost currency's inventory snapshot. */
export const inventoryCurrencyStatsSchema = z.object({
  isoCode: isoCurrencyCodeSchema,
  assetCount: z.number(),
  assetsWithCompleteAccounting: z.number(),
  assetsWithIncompleteAccounting: z.number(),
  weightedInventoryDays: z.number().nullable(),
  weightedUsefulLife: z.number().nullable(),
  totalBookValue: z.string().nullable(),
  totalDepreciableAmount: z.string().nullable(),
  totalExpectedSalvageValue: z.string().nullable(),
});

export type InventoryCurrencyStats = z.infer<
  typeof inventoryCurrencyStatsSchema
>;

// The stats live under the response envelope's `data`; assets are stripped here and
// validated separately in chunks below.
const inventorySnapshotStatsResponseSchema = z.object({
  data: inventoryCurrencyStatsSchema,
  metadata: responseMetadataSchema,
});

/** A single currency's inventory snapshot: backend stats plus its assets. */
export interface InventoryAssetsSnapshot extends InventoryCurrencyStats {
  assets: InventoryAssetSnapshotItem[];
}

// The raw wire response keeps assets as an unparsed array so they can be validated
// in chunks (snapshots can be large); the stats are validated up front.
interface RawInventoryAssetsSnapshotResponse {
  data: InventoryCurrencyStats & {
    assets: InventoryAssetSnapshotItem[];
  };
}

interface InventoryAssetsSnapshotParameters {
  asOfDate: DateTime;
  currency: IsoCurrencyCode;
}

/**
 * Retrieves a snapshot of inventory assets as of a specific date for a single
 * cost currency.
 *
 * Note: Date validation should be handled at the component layer.
 * This API function assumes the DateTime object is valid when passed in.
 *
 * @param params - The parameters for the snapshot request
 * @returns A promise resolving to the single-currency inventory snapshot
 */
export async function getInventoryAssetsSnapshot(
  params: InventoryAssetsSnapshotParameters
): Promise<InventoryAssetsSnapshot> {
  const asOfDateString = params.asOfDate.toISODate();

  const path = '/v1/report/asset/inventory-snapshot';
  const response = await api.get<RawInventoryAssetsSnapshotResponse>(path, {
    params: {
      asOfDate: asOfDateString,
      currency: params.currency,
    },
  });

  const parsed = await parseResponse(
    inventorySnapshotStatsResponseSchema,
    response.data,
    { schemaName: 'InventoryAssetsSnapshotStats', endpoint: path }
  );
  const stats = parsed.data;
  const assets = await parseArrayInChunks(
    response.data.data.assets,
    inventoryAssetSnapshotItemSchema,
    {
      schemaValidation: {
        schemaName: 'InventoryAssetSnapshotItem',
        endpoint: path,
        extra: {
          asOfDate: asOfDateString,
          isoCode: stats.isoCode,
        },
      },
    }
  );
  return { ...stats, assets };
}
